import { useMemo } from 'react';
import { css } from '@linaria/core';
import { ResponsiveProps } from '@cbhq/cds-web';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { Avatar } from '@cbhq/cds-web/media';
import { TextHeadline, TextLegal, TextTitle1, TextTitle4 } from '@cbhq/cds-web/typography';
import { ProgressBar } from '@cbhq/cds-web/visualizations/ProgressBar';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import CDPQuestLogo from ':cloud/widgets/buildQuest/assets/cdpQuestLogo.png';

const yellow70 = 'rgb(var(--yellow70))';

function getAbbreviatedName(fullName: string) {
  const nameParts = fullName.split(' ');

  if (nameParts.length === 1) {
    return nameParts[0];
  }

  return `${nameParts[0]} ${nameParts[nameParts.length - 1][0]}.`;
}

const responsiveBoxConfig: ResponsiveProps = {
  phone: { flexDirection: 'column', gap: 4 },
  tablet: { flexDirection: 'column', gap: 4 },
  desktop: { flexDirection: 'row' },
};

const playerName = 'Amanda McDermott';
const playerLevel = 'Level 2';

export function PlayerPanel() {
  const { isPhone, isTablet, isDesktop } = useSimpleBreakpoints();

  const progressBar = css`
    [data-testid='cds-progress-bar-inner-bar'] {
      background-color: ${yellow70} !important;
      height: 8px !important;
    }
    [data-testid='cds-progress-bar-inner-bar-container'] {
      height: 8px !important;
    }
  `;
  const backgroundBlur = css`
    backdrop-filter: blur(20px);
  `;

  const boxSpacingVertical = useMemo(() => (isPhone ? 2 : 5), [isPhone]);
  const boxSpacingHorizontal = useMemo(() => (isPhone ? 4 : 5), [isPhone]);
  const gridTemplateColumns = useMemo(() => (isDesktop ? '1fr 3fr 1fr' : '1fr 1fr'), [isDesktop]);
  const imageHeight = useMemo(() => (isPhone ? 40 : undefined), [isPhone]);
  const avatarSize = useMemo(() => (isPhone ? 'xl' : 'xxxl'), [isPhone]);

  return (
    <Box
      className={backgroundBlur}
      position="sticky"
      bottom={0}
      width="100%"
      alignItems="center"
      justifyContent="space-between"
      dangerouslySetBackground="rgba(0, 0, 0, 0.5)"
      spacingVertical={boxSpacingVertical}
      spacingHorizontal={boxSpacingHorizontal}
      responsiveConfig={responsiveBoxConfig}
    >
      {(isPhone || isTablet) && (
        <VStack className={progressBar} justifyContent="center" width="100%" gap={2}>
          <ProgressBar weight="heavy" progress={0.37} />
          <HStack justifyContent="space-between">
            <TextTitle4 as="h4" dangerouslySetColor={yellow70}>
              +500 EXP
            </TextTitle4>
            <TextTitle4 as="h4" color="foregroundMuted">
              1000 exp to level 3
            </TextTitle4>
          </HStack>
        </VStack>
      )}
      <Grid width="100%" templateColumns={gridTemplateColumns} gap={3} justifyContent="center">
        <img src={CDPQuestLogo} alt="CDP Quest" height={imageHeight} width="auto" />
        {isDesktop && (
          <VStack className={progressBar} justifyContent="center" gap={2}>
            <ProgressBar weight="heavy" progress={0.37} />
            <HStack justifyContent="space-between">
              <TextTitle4 as="h4" dangerouslySetColor={yellow70}>
                +500 EXP
              </TextTitle4>
              <TextTitle4 as="h4" color="foregroundMuted">
                1000 exp to level 3
              </TextTitle4>
            </HStack>
          </VStack>
        )}
        <HStack gap={3} justifyContent="flex-end" width="100%">
          <VStack>
            {isPhone ? (
              <TextHeadline as="h2" overflow="truncate">
                {getAbbreviatedName(playerName)}
              </TextHeadline>
            ) : (
              <TextTitle1 as="h2" overflow="truncate">
                {getAbbreviatedName(playerName)}
              </TextTitle1>
            )}
            <HStack gap={1} alignItems="center">
              {isPhone ? (
                <TextLegal as="p" dangerouslySetColor={yellow70}>
                  {playerLevel}
                </TextLegal>
              ) : (
                <TextTitle4 as="p" dangerouslySetColor={yellow70}>
                  {playerLevel}
                </TextTitle4>
              )}
              <Icon name="nftSaleActive" size="s" dangerouslySetColor={yellow70} />
            </HStack>
          </VStack>
          <Avatar size={avatarSize} alt={`${playerName}'s avatar`} name={playerName} />
        </HStack>
      </Grid>
    </Box>
  );
}
